import { makeAutoObservable, runInAction, toJS } from 'mobx';
import posthog from 'posthog-js';
import { uniqueId } from 'lodash-es';
import { stream } from 'fetch-event-stream';
import type {
  AIRequest,
  AIResponse,
  AIStreamErrorEvent,
  InputItem,
  SqlRunResponse,
} from '@instamenu/ai-api';
import { captureClientException } from '@instamenu/posthog/client';
import { DateTime } from 'luxon';
import type { ChatHistoryItem, AIFunctions, AIFunctionOutput } from '../types';
import {
  type AttachmentInput,
  type Attachment,
  createAttachment,
  attachmentToInputItems,
} from './attachment-utils';
import { threads } from './threads';
import { readPendingSqlRuns, writePendingSqlRuns } from './sql-run-storage';

export type AssistantThreadData = { items: ChatHistoryItem[]; id: string; conversationId: string };

const PROMPT_META_PROPERTY_MAX_LENGTH = 200;
const PROMPT_META_PROPERTY_KEYS = [
  'intakeChatPromptVersion',
  'intakeFlow',
  'intakeLeadId',
  'intakeName',
  'intakePhone',
  'intakePhoneValid',
  'restaurantCity',
  'restaurantName',
] as const;

export type ThreadOptions<C = any> = {
  assistant: 'intake' | 'dashboard' | 'intakeChat';
  additionalHeaders?: () => Promise<Record<string, string>>;
  functions: AIFunctions<C>;
  functionContext?: () => C;
  conversationId?: string;
  pendingTitle?: string;
  draftId?: string;
};

function createClientThreadId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `client:${crypto.randomUUID()}`
    : uniqueId('client:');
}

class AIStreamError extends Error {
  constructor(
    message: string,
    readonly serverCaptured: boolean,
  ) {
    super(message);
  }
}

export class Thread {
  emitter = new EventTarget();
  private _isBusy = 0;
  private abortController = new AbortController();

  private inputQueue: InputItem[] = [];
  pendingSqlRuns: SqlRunResponse[] = [];
  sqlRunError?: string;
  private restoringSqlRuns = false;
  private cancelingSqlRuns?: Promise<void>;
  private sqlCancellationRequested = false;
  options: ThreadOptions;
  private _id?: string;
  private clientThreadId = createClientThreadId();

  conversationId?: string;
  conversationTitle?: string;

  started = false;
  attachments: Record<string, Attachment> = {}; // base64 encoded attachments that are not yet sent to the AI

  // todo: simplify this data structure and match the transport shape more closely?
  history: ChatHistoryItem[] = [];

  // todo: move the notepad out of the thread?
  notepad = '';
  canvas?: 'menu' | 'notepad';

  // todo: combine these two into one
  context: Record<string, unknown> = {};
  meta: Record<string, string> = {}; // arbitrary extra key/value pairs that are sent to the backend and may be used by the assistant
  analyticsMeta: Record<string, string> = {}; // sanitized extra key/value pairs used only for analytics

  editingMessageId: string | null = null;
  editingContent = '';

  constructor(options: ThreadOptions) {
    this.options = options;
    this.clientThreadId = options.draftId ?? this.clientThreadId;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private get promptProperties() {
    return {
      ...Object.fromEntries(
        PROMPT_META_PROPERTY_KEYS.flatMap((key) =>
          typeof this.analyticsMeta[key] === 'string'
            ? [[key, this.analyticsMeta[key].slice(0, PROMPT_META_PROPERTY_MAX_LENGTH)]]
            : [],
        ),
      ),
      assistant: this.options.assistant,
      clientThreadId: this.clientThreadId,
      conversationId: this.conversationId,
      threadId: this.id,
    };
  }

  get previousResponseId() {
    return this.history
      .flatMap((item) => (item.role === 'assistant' && item.status === 'completed' ? [item] : []))
      .reverse()
      .at(0)?.responseId;
  }

  private getRecoveredFunctionCallOutputs(): InputItem[] {
    if (this.inputQueue.length > 0) return [];
    // Rehydrated dashboard history can lose the original OpenAI response grouping,
    // so we recover the contiguous trailing assistant tool calls instead.
    if (!this.previousResponseId) return [];

    const recoveredOutputs: InputItem[] = [];

    for (let index = this.history.length - 1; index >= 0; index -= 1) {
      const item = this.history[index];

      if (item.role !== 'assistant') break;
      if (item.type === 'reasoning') continue;
      if (
        item.type !== 'function_call' ||
        item.status !== 'completed' ||
        item.sqlRunState === 'submitted'
      )
        break;
      if (item.name === 'web_search' || item.name === 'image_generation') continue;
      if (item.output == null) break;

      recoveredOutputs.unshift({
        type: 'function_call_output',
        callId: item.id,
        output: item.output,
      });
    }

    return recoveredOutputs;
  }

  importHistory(data: AssistantThreadData) {
    this.history = data.items;
    this.conversationId = data.conversationId;
    this.id = data.id;
  }

  private get sqlRunStorageKey() {
    return `ai-sql-runs:${this.context.userId}:${this.context.restaurantId}:${this.id ?? this.clientThreadId}`;
  }

  private persistSqlRuns() {
    if (
      this.options.assistant === 'dashboard' &&
      this.context.userId &&
      this.context.restaurantId
    ) {
      writePendingSqlRuns(this.sqlRunStorageKey, this.pendingSqlRuns);
    }
  }

  restoreSqlRuns() {
    if (
      this.options.assistant !== 'dashboard' ||
      !this.context.userId ||
      !this.context.restaurantId
    )
      return;
    const pending = readPendingSqlRuns(this.sqlRunStorageKey);
    this.pendingSqlRuns = pending;
    for (const run of pending) this.applySqlRun(run);
    if (pending.length) {
      if (!this.id) threads.registerPending(this);
      void this.resumeSqlRuns();
    }
  }

  private applySqlRun(data: SqlRunResponse) {
    this.conversationId = data.conversationId;
    if (data.state === 'submitted') {
      this.inputQueue = this.inputQueue.filter(
        (item) => item.type !== 'function_call_output' || item.callId !== data.callId,
      );
    }
    const saved = {
      ...data,
      expiresAt: data.expiresAt ?? new Date(Date.now() + 24 * 60 * 60_000).toISOString(),
    };
    this.pendingSqlRuns = [
      ...this.pendingSqlRuns.filter((run) => run.callId !== data.callId),
      saved,
    ];
    let item = this.history.find(
      (item) => item.type === 'function_call' && item.id === data.callId,
    );
    if (!item) {
      item = {
        type: 'function_call',
        role: 'assistant',
        id: data.callId,
        name: 'query_database',
        status: 'incomplete',
      };
      this.history.push(item);
      item = this.history[this.history.length - 1];
    }
    if (item.type === 'function_call' && item.role === 'assistant') {
      item.responseId = data.responseId;
      item.arguments = data.arguments;
      item.sqlRunState = data.state;
      item.status = data.state === 'submitted' ? 'completed' : 'incomplete';
      item.errorText = undefined;
    }
    this.persistSqlRuns();
  }

  async resumeSqlRuns() {
    if (this.isBusy || this.restoringSqlRuns || !this.pendingSqlRuns.length) return;
    this.restoringSqlRuns = true;
    this.sqlRunError = undefined;
    try {
      for (const run of [...this.pendingSqlRuns]) {
        if (!this.pendingSqlRuns.includes(run)) continue;
        const response = await this.fetchAIStream([], run.responseId, {
          responseId: run.responseId,
          callId: run.callId,
          continuationResponseId: run.continuationResponseId,
        });
        if (response.error) {
          this.sqlRunError = response.error.message;
          return;
        }
      }
      if (this.hasPendingAutoInput()) await this.post({});
    } finally {
      runInAction(() => {
        this.restoringSqlRuns = false;
      });
    }
  }

  disconnect() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this._isBusy = 0;
  }

  async post(data: { message?: string; attachments?: AttachmentInput[] }, skipHistory = false) {
    if (this.cancelingSqlRuns) await this.cancelingSqlRuns;
    if (
      this.sqlCancellationRequested &&
      this.pendingSqlRuns.some(
        (run) => !['submitted', 'succeeded', 'failed', 'canceled'].includes(run.state),
      )
    )
      return false;
    this.sqlCancellationRequested = false;
    if (!this.restoringSqlRuns && this.pendingSqlRuns.some((run) => run.state === 'submitted')) {
      await this.resumeSqlRuns();
      if (this.pendingSqlRuns.some((run) => run.state === 'submitted')) return false;
    }
    if (data.message)
      posthog.capture('ai_prompt', {
        ...this.promptProperties,
        message: data.message,
      });

    const attachments = data.attachments?.map(createAttachment) ?? [];
    attachments.forEach((attachment) => (this.attachments[attachment.id] = attachment));

    const recoveredFunctionCallOutputs = this.getRecoveredFunctionCallOutputs();
    const inputs: InputItem[] = [
      ...recoveredFunctionCallOutputs,
      ...this.inputQueue,
      ...attachments.flatMap(attachmentToInputItems),
    ];
    this.inputQueue = [];

    if (!skipHistory) {
      attachments.forEach((attachment) => {
        if (attachment.kind === 'image')
          this.history.push({
            type: 'message',
            id: attachment.id,
            role: 'user',
            content: {
              type: 'image',
              id: uniqueId(),
              name: attachment.name,
              url: attachment.data,
              partial: false,
            },
          });
        else
          this.history.push({
            type: 'message',
            id: attachment.id,
            role: 'user',
            content: {
              type: 'file',
              id: uniqueId(),
              name: attachment.name,
              mimeType: attachment.mimeType,
              url: attachment.data,
            },
          });
      });
      if (data.message)
        this.history.push({
          id: uniqueId(),
          type: 'message',
          role: 'user',
          content: { text: data.message, type: 'text' },
        });
    }

    if (!this.id && !skipHistory && (data.message || attachments.length > 0)) {
      threads.registerPending(this);
    }

    if (data.message) {
      inputs.push({
        role: 'user',
        type: 'message',
        content: [{ type: 'text', text: data.message }],
      });
    }

    console.log('using previous id:', this.previousResponseId);
    console.log('with inputs:', toJS(inputs));

    const res = await this.fetchAIStream(inputs, this.previousResponseId);

    console.log('response:', toJS(res));
    console.log('queue after fetch:', toJS(this.inputQueue));

    if (!res.error) {
      if (this.hasPendingAutoInput()) this.post({});
      // } else if (res.error.name==='AbortError') {
    } else if ((res.error as Error).name === 'AbortError' && !this.pendingSqlRuns.length) {
      console.log('Error fetching AI response:', res.error);
      const messages = this.history.filter((i) => i.type === 'message' && i.role === 'assistant');
      const lastCompletedAssistantMessageIndex = messages.findIndex(
        (i) => i.status === 'completed',
      );
      const incompleteAssistantTextMessages = messages
        .slice(lastCompletedAssistantMessageIndex + 1)
        .flatMap((item) =>
          item.content.type === 'text'
            ? [
                {
                  type: 'message' as const,
                  role: item.role,
                  content: [{ type: 'text' as const, text: item.content.text }],
                },
              ]
            : [],
        );
      this.inputQueue.unshift(...incompleteAssistantTextMessages);
      this.inputQueue.unshift(...inputs);
    }
    console.log('queue after error handling:', toJS(this.inputQueue));
    return true;
  }

  async postSystemMessage(data: { message: string }) {
    posthog.capture('ai_developer_prompt', {
      ...this.promptProperties,
      message: data.message,
    });

    this.history.push({
      id: uniqueId(),
      type: 'message',
      role: 'developer',
      content: { text: data.message, type: 'text' },
    });

    const res = await this.fetchAIStream(
      [
        {
          role: 'developer',
          type: 'message',
          content: [{ type: 'text', text: data.message }],
        },
      ],
      this.previousResponseId,
    );

    if (!res.error) {
      if (this.hasPendingAutoInput()) this.post({});
    } else if ((res.error as Error).name === 'AbortError') {
      console.log('Error fetching AI response:', res.error);
    }
    console.log('queue after error handling:', toJS(this.inputQueue));
    return true;
  }

  capturePromptEditRerun(data: { originalMessage: string; editedMessage: string }) {
    posthog.capture('ai_prompt_edit_rerun', {
      ...this.promptProperties,
      originalMessage: data.originalMessage,
      editedMessage: data.editedMessage,
    });
  }

  async fetchAIStream(
    input: InputItem[],
    previousResponseId?: string,
    resumeSqlRun?: AIRequest['resumeSqlRun'],
  ): Promise<{ error?: Error }> {
    const signal = this.abortController.signal;
    const body: AIRequest = {
      input,
      resumeSqlRun,
      sqlRunContinuation: this.pendingSqlRuns.find((run) =>
        input.some((item) => item.type === 'function_call_output' && item.callId === run.callId),
      ),
      assistant: this.options.assistant,
      conversationId: this.conversationId,
      previousResponseId,
      context: {
        ...this.context,
        analyticsMeta: this.analyticsMeta,
        clientThreadId: this.clientThreadId,
        meta: this.meta,
      },
    };
    this._isBusy++;

    try {
      const events = await stream('/api/ai/stream', {
        method: 'POST',
        headers: {
          'X-User-Time': DateTime.local().toISO(),
          ...((await this.options?.additionalHeaders?.()) ?? {}),
        },
        body: JSON.stringify(body),
        signal,
      });

      for await (const event of events) {
        signal.throwIfAborted();
        if (event.event === 'error') {
          let errorEvent: Partial<AIStreamErrorEvent> | null = null;
          try {
            errorEvent = JSON.parse(event.data ?? '{}') as Partial<AIStreamErrorEvent> | null;
          } catch {
            errorEvent = null;
          }
          throw new AIStreamError(
            typeof errorEvent?.message === 'string' && errorEvent.message
              ? errorEvent.message
              : this.pendingSqlRuns.length
                ? 'The connection was interrupted. Resume the saved report to continue.'
                : 'The request was interrupted. Please try again.',
            errorEvent?.captured === true,
          );
        }
        if (event.data) {
          try {
            const data = JSON.parse(event.data) as AIResponse;
            console.log('AI response:', data.responseId);
            await this.onAIResponse(data);
          } catch (e) {
            captureClientException(e, {
              source: 'ai-ui.thread.parse-response',
              properties: {
                assistant: this.options.assistant,
                conversationId: this.conversationId,
                eventDataLength: event.data.length,
                previousResponseId,
                threadId: this.id,
              },
            });
          }
        }
      }

      return { error: undefined };
    } catch (e) {
      const error = e as Error;
      if (
        error.name !== 'AbortError' &&
        !(error instanceof AIStreamError && error.serverCaptured)
      ) {
        captureClientException(e, {
          source: 'ai-ui.thread.fetch-stream',
          properties: {
            assistant: this.options.assistant,
            conversationId: this.conversationId,
            previousResponseId,
            threadId: this.id,
          },
        });
      }
      if (this.pendingSqlRuns.length && error.name !== 'AbortError')
        this.sqlRunError = error.message;
      return { error };
    } finally {
      runInAction(() => {
        if (signal === this.abortController.signal) this._isBusy = Math.max(0, this._isBusy - 1);
      });
    }
  }

  private appendToHistory(item: ChatHistoryItem) {
    this.history.push(item);
  }

  async onAIResponse(data: AIResponse) {
    switch (data.type) {
      case 'sql_run':
        this.applySqlRun(data);
        break;
      case 'response.completed':
        this.pendingSqlRuns = this.pendingSqlRuns.filter(
          (run) =>
            !(
              run.state === 'submitted' &&
              (run.continuationResponseId ?? run.responseId) === data.responseId
            ),
        );
        this.persistSqlRuns();
        this.history = this.history.map((item) =>
          item.role === 'assistant' && item.responseId === data.responseId
            ? { ...item, status: 'completed' }
            : item,
        );
        if (!this.conversationId) this.conversationId = data.conversationId;
        break;
      case 'response.incomplete':
      case 'response.failed':
        if (data.type === 'response.failed' && data.code === 'REPORT_ANSWER_INTERRUPTED')
          this.sqlRunError = data.message;
        this.pendingSqlRuns = this.pendingSqlRuns.filter(
          (run) =>
            run.continuationResponseId !== data.responseId &&
            !(run.state === 'submitted' && run.responseId === data.responseId),
        );
        this.persistSqlRuns();
        this.history = this.history.map((item) =>
          item.role === 'assistant' && item.responseId === data.responseId
            ? { ...item, status: 'failed' }
            : item,
        );
        break;
      case 'text.replace': {
        const item = this.history.find(({ id }) => id === data.itemId);
        if (item?.type === 'message' && item.content.type === 'text') {
          item.content.text = data.text;
          if (item.role === 'assistant') item.responseId = data.responseId;
        } else {
          this.appendToHistory({
            type: 'message',
            id: data.itemId,
            responseId: data.responseId,
            role: 'assistant',
            content: { text: data.text, type: 'text' },
            status: 'incomplete',
          });
        }
        break;
      }
      case 'text.delta': {
        const item = this.history.find(({ id }) => id === data.itemId);
        if (item?.type === 'message' && item.content.type === 'text')
          item.content.text += data.delta;
        else {
          this.appendToHistory({
            type: 'message',
            id: data.itemId,
            responseId: data.responseId,
            role: 'assistant',
            content: { text: data.delta, type: 'text' },
            status: 'incomplete',
          });
        }
        break;
      }
      case 'reasoning.summary.delta': {
        const item = this.history.find(({ id }) => id === data.itemId);
        if (item?.type === 'reasoning') item.content.text += data.delta;
        else {
          this.appendToHistory({
            type: 'reasoning',
            id: data.itemId,
            responseId: data.responseId,
            role: 'assistant',
            content: { text: data.delta },
            status: 'incomplete',
          });
        }
        break;
      }
      case 'function_call': {
        const isBuiltInTool = data.name === 'web_search' || data.name === 'image_generation';
        const findFunctionCall = (callId: string) => {
          const item = this.history.find(
            (item) => item.type === 'function_call' && item.id === callId,
          );
          return item?.type === 'function_call' ? item : undefined;
        };
        switch (data.status) {
          case 'started':
            this.appendToHistory({
              type: 'function_call',
              id: data.callId,
              responseId: data.responseId,
              role: 'assistant',
              name: data.name,
              descriptionKey: data.descriptionKey ?? `aiAssistant.functionCalls.${data.name}`,
              arguments: data.arguments,
              status: 'incomplete',
            });
            posthog.capture('assistant_function_called', {
              name: data.name,
              callId: data.callId,
              responseId: data.responseId,
              assistant: this.options.assistant,
              restaurantId: this.context?.restaurantId,
            });
            break;
          case 'complete': {
            const completeItem = findFunctionCall(data.callId);
            if (completeItem && completeItem.role === 'assistant') {
              runInAction(() => {
                completeItem.arguments = data.arguments;
                completeItem.output = data.output;
                completeItem.status = 'completed';
              });
            }
            if (
              !isBuiltInTool &&
              !this.inputQueue.some(
                (item) => item.type === 'function_call_output' && item.callId === data.callId,
              )
            ) {
              this.inputQueue.unshift({
                callId: data.callId,
                output: data.output,
                type: 'function_call_output',
              });
            }
            break;
          }
          case 'skipped': {
            const skippedItem = findFunctionCall(data.callId);
            if (!skippedItem || skippedItem.role !== 'assistant') break;

            const args = JSON.parse(data.arguments);
            console.log(`Invoking function ${data.name} with`, args);
            let output: AIFunctionOutput = { success: false };
            try {
              const fn = this.options.functions[data.name]?.fn;
              if (fn) {
                output = await fn(
                  this,
                  JSON.parse(data.arguments),
                  this.options.functionContext?.(),
                );
              }
              console.log('Function response:', output);
              runInAction(() => {
                skippedItem.arguments = data.arguments;
                skippedItem.output = JSON.stringify(output);
                skippedItem.status = 'completed';
              });
            } catch (e) {
              captureClientException(e, {
                source: 'ai-ui.thread.invoke-function',
                properties: {
                  assistant: this.options.assistant,
                  callId: data.callId,
                  functionName: data.name,
                  responseId: data.responseId,
                  threadId: this.id,
                },
              });
              runInAction(() => {
                skippedItem.errorText = e instanceof Error ? e.message : String(e);
                skippedItem.status = 'failed';
              });
            }

            const { additionalInput = [], ...rest } = output;

            // prepend function call outputs to the input queue
            this.inputQueue.unshift({
              callId: data.callId,
              output: JSON.stringify(rest),
              type: 'function_call_output',
            });
            this.inputQueue.push(...additionalInput);
            break;
          }
          default:
            console.warn('Unknown function call status:', data);
        }
        break;
      }
      case 'image': {
        const item = this.history.find(({ id, type }) => id === data.callId && type === 'message');
        let contentId;
        const [, extension] = data.mimeType.split('/');
        const imageData =
          data.url ?? (data.base64 ? `data:${data.mimeType};base64,${data.base64}` : '');
        if (item?.type === 'message' && item.content.type === 'image') {
          item.content.partial = !!data.partial;
          item.content.url = imageData;
          contentId = item.content.id;
        } else {
          const content = {
            type: 'image' as const,
            id: uniqueId(),
            url: imageData,
            partial: !!data.partial,
          };
          contentId = content.id;
          this.appendToHistory({
            type: 'message',
            id: data.callId,
            responseId: data.responseId,
            role: 'assistant',
            content,
            status: 'incomplete',
          });
          this.inputQueue.push({
            type: 'message',
            role: 'developer',
            content: [
              { type: 'text', text: `<previous_image_id>${content.id}</previous_image_id>` },
            ],
          });
        }
        this.attachments[contentId] = {
          id: contentId,
          data: imageData,
          mimeType: data.mimeType,
          kind: 'image',
          name: `image.${extension}`,
        };
        break;
      }
      default:
        console.warn('Unknown AI response type:', data);
    }
    const lastItem = this.history[this.history.length - 1];
    if (
      lastItem?.role === 'assistant' &&
      lastItem.status === 'completed' &&
      lastItem.type === 'message' &&
      data.type === 'response.completed'
    ) {
      // wait until the first complete assistant text message to set the conversation ID
      threads.add(this);
    }
  }

  get isBusy() {
    return this._isBusy !== 0;
  }

  /** Returns true when the input queue has items that should be auto-sent without waiting for user input.
   * This includes function call outputs (from developer-defined tools) and developer messages
   * (e.g. the <previous_image_id> message enqueued after a built-in image_generation call). */
  private hasPendingAutoInput() {
    const firstQueueItem = this.inputQueue.at(0);
    return (
      firstQueueItem?.type === 'function_call_output' ||
      (firstQueueItem?.type === 'message' && firstQueueItem.role === 'developer')
    );
  }

  showCanvas(name: 'menu' | 'notepad') {
    this.canvas = name;
  }

  hideCanvas() {
    this.canvas = undefined;
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.post({ message: 'hi' }, true);
  }

  resetToIndex(index: number) {
    this.stop();
    this.history = this.history.slice(0, index);
    this.inputQueue = [];

    // Clean up orphaned attachments that are no longer referenced in history
    const referencedAttachmentIds = new Set<string>();
    for (const item of this.history) {
      if (item.id) referencedAttachmentIds.add(item.id);
      if (
        item.type === 'message' &&
        (item.content.type === 'image' || item.content.type === 'file')
      ) {
        referencedAttachmentIds.add(item.content.id);
      }
    }

    this.attachments = Object.fromEntries(
      Object.entries(this.attachments).filter(([id]) => referencedAttachmentIds.has(id)),
    );
  }

  stop() {
    const pending = this.pendingSqlRuns.filter(
      (run) => !['submitted', 'succeeded', 'failed', 'canceled'].includes(run.state),
    );
    if (pending.length && !this.cancelingSqlRuns) {
      this.sqlCancellationRequested = true;
      this.cancelingSqlRuns = this.cancelSqlRuns(pending).finally(() => {
        runInAction(() => {
          this.cancelingSqlRuns = undefined;
        });
      });
    }
    this.abortController.abort();
    this.abortController = new AbortController();
    this._isBusy = 0;
    // mark incomplete bot messages as failed
    this.history = this.history.map((item) =>
      item.role === 'assistant' && item.status === 'incomplete'
        ? { ...item, status: 'failed' }
        : item,
    );
  }

  private async cancelSqlRuns(pending: SqlRunResponse[]) {
    const restaurantId = this.context.restaurantId;
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(await this.options.additionalHeaders?.()),
      };
      for (const run of pending) {
        const response = await fetch('/api/ai/sql-runs/cancel', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            responseId: run.responseId,
            callId: run.callId,
            restaurantId,
            runId: run.runId,
          }),
        });
        const unavailable = response.status === 404 || response.status === 410;
        if (!response.ok && !unavailable) {
          const failure = await response.json().catch(() => ({}));
          throw new Error(
            failure.error ??
              'Unable to confirm cancellation. Resume the report and try canceling again.',
          );
        }
        const result = unavailable
          ? {
              state: 'failed',
              expires_at: run.expiresAt,
              error: { code: 'REPORT_UNAVAILABLE', message: 'This report is no longer available.' },
            }
          : await response.json();
        const output = JSON.stringify(
          result.state === 'succeeded'
            ? { status: result.state, ...result.result }
            : { status: result.state, error: result.error },
        );
        runInAction(() => {
          this.applySqlRun({ ...run, state: result.state, expiresAt: result.expires_at });
        });
        await this.onAIResponse({
          type: 'function_call',
          responseId: run.responseId,
          callId: run.callId,
          name: 'query_database',
          arguments: run.arguments,
          status: 'complete',
          output,
        });
        if (unavailable)
          runInAction(() => {
            this.pendingSqlRuns = this.pendingSqlRuns.filter(
              (saved) => saved.callId !== run.callId,
            );
          });
      }
      runInAction(() => {
        this.persistSqlRuns();
      });
    } catch (error) {
      runInAction(() => {
        this.sqlRunError = (error as Error).message;
      });
    }
  }

  startEditing(messageId: string) {
    const message = this.history.find((item) => item.id === messageId);
    if (message?.type === 'message' && message.content.type === 'text') {
      this.editingMessageId = messageId;
      this.editingContent = message.content.text;
    }
  }

  cancelEditing() {
    this.editingMessageId = null;
    this.editingContent = '';
  }

  updateEditingContent(content: string) {
    this.editingContent = content;
  }

  async saveEditedMessage(messageId: string) {
    const messageIndex = this.history.findIndex((item) => item.id === messageId);
    if (messageIndex === -1) return;

    const message = this.history[messageIndex];
    if (message.type !== 'message' || message.content.type !== 'text') return;

    this.capturePromptEditRerun({
      originalMessage: message.content.text,
      editedMessage: this.editingContent,
    });

    this.resetToIndex(messageIndex);

    const editedContent = this.editingContent;
    this.cancelEditing();

    await this.post({ message: editedContent });
  }

  get id() {
    return this._id;
  }
  set id(value: string | undefined) {
    const previousKey = this.sqlRunStorageKey;
    this._id = value;
    if (this.pendingSqlRuns.length) {
      this.persistSqlRuns();
      if (previousKey !== this.sqlRunStorageKey) writePendingSqlRuns(previousKey, []);
    }
    this.emitter.dispatchEvent(new Event('onId'));
  }
}
